import { store } from '../state/store';
import type { Book, OpenOrder, ApiResponse } from '../types';
import { notifications } from '../ui/notifications';

interface LoadOrdersParams {
  currentAssetIds: string[];
  listOpenOrders: (filters?: { asset_ids?: string[] }) => Promise<ApiResponse<OpenOrder[]>>;
  ordersEl: HTMLElement;
  books: Map<string, Book>;
  cancelOrder: (orderId: string) => Promise<ApiResponse>;
  flash: (type: 'ok' | 'err' | '', message: string) => void;
}

interface RenderOrdersParams {
  ordersEl: HTMLElement;
  books: Map<string, Book>;
  cancelOrder: (orderId: string) => Promise<ApiResponse>;
  reload: () => void;
  flash: (type: 'ok' | 'err' | '', message: string) => void;
}

export async function loadOrders({
  currentAssetIds,
  listOpenOrders: _listOpenOrders,
  ordersEl,
  books,
  cancelOrder: _cancelOrder,
  flash: _flash,
}: LoadOrdersParams): Promise<OpenOrder[] | null> {
  try {
    const filters = Array.isArray(currentAssetIds) && currentAssetIds.length > 0 ? { asset_ids: currentAssetIds } : {};
    const resp = await _listOpenOrders(filters);
    const orders = (resp && resp.ok && Array.isArray(resp.data)) ? resp.data : [];
    renderOrders(orders, { ordersEl, books, cancelOrder: _cancelOrder, reload: () => loadOrders({ currentAssetIds, listOpenOrders: _listOpenOrders, ordersEl, books, cancelOrder: _cancelOrder, flash: _flash }), flash: _flash });
    try {
      const idx = new Set<string>();
      for (const o of orders) {
        const assetId = o.asset_id; const side = (o.side || '').toUpperCase(); const priceKey = Number(o.price);
        if (!assetId || !(priceKey > 0) || (side !== 'BUY' && side !== 'SELL')) continue;
        const dec = store?.priceDecimalsById?.[assetId] ?? 2;
        const key = `${assetId}|${side}|${priceKey.toFixed(dec)}`;
        idx.add(key);
      }
      store.openOrdersIndex = idx;
    } catch {}
    return orders;
  } catch (e) {
    const error = e as Error; ordersEl.textContent = 'Error: ' + (error?.message ?? e); return null;
  }
}

export function renderOrders(list: OpenOrder[], { ordersEl, books, cancelOrder: _cancelOrder, reload, flash: _flash }: RenderOrdersParams): void {
  if (!Array.isArray(list) || list.length === 0) { ordersEl.textContent = 'No open orders'; return; }
  const container = document.createElement('div');
  container.style.display = 'grid';
  container.style.gap = '10px';
  container.style.fontSize = '16px';
  container.style.lineHeight = '1.3';
  for (const o of list) {
    const row = document.createElement('div');
    row.style.display = 'grid';
    row.style.gridTemplateColumns = 'auto 1fr auto';
    row.style.gap = '12px';
    row.style.alignItems = 'center';
    row.style.padding = '8px 10px';
    row.style.border = '1px solid var(--border)';
    row.style.borderRadius = '6px';
    const side = document.createElement('div');
    side.textContent = o.side;
    try {
      const panel = document.querySelector(`.panel[data-asset-id="${o.asset_id}"]`);
      if ((panel as any)?.classList?.contains('team-a')) side.style.color = 'var(--teamA)';
      else if ((panel as any)?.classList?.contains('team-b')) side.style.color = 'var(--teamB)';
      else side.className = o.side === 'BUY' ? 'bid' : 'ask';
    } catch { side.className = o.side === 'BUY' ? 'bid' : 'ask'; }
    side.style.fontWeight = '700'; side.style.fontSize = '18px';
    const team = document.createElement('div');
    const book = books.get(o.asset_id);
    const name = book?.name || (o.asset_id ? o.asset_id.slice(0, 10) + '…' : '');
    const priceStr = Number(o.price).toFixed(2);
    const sizeStr = Number(o.original_size).toFixed(2);
    team.textContent = `${name}  —  @ ${priceStr} • ${sizeStr} shares`;
    const btn = document.createElement('button');
    btn.textContent = 'Cancel';
    btn.style.padding = '4px 10px';
    btn.onclick = async () => {
      btn.disabled = true;
      try {
        const res = await _cancelOrder(o.id);
        if (res?.ok) { notifications.success('Order cancelled successfully!', 2000); if (typeof reload === 'function') reload(); }
      } finally { btn.disabled = false; }
    };
    row.appendChild(side);
    row.appendChild(team);
    row.appendChild(btn);
    container.appendChild(row);
  }
  ordersEl.innerHTML = '';
  ordersEl.appendChild(container);
}
